import { useSharedView } from "@/features/sharedViews/api/useSharedViews";
import { parseSharedRouteId } from "@/features/sharedViews/types";
import type { ChangeEvent } from "react";
import { useEffect, useMemo, useState, useRef } from "react";
import { useDebounce } from "@/hooks/useDebounce";

/**
 * How long the search input idles before its value is pushed to Redux
 * (and from there to the leads-list API request). 500 ms still feels
 * native — you stop typing, the result lands a beat later — but throttles
 * harder than the old 300 ms so a normal-speed typist fires fewer
 * (heavier, scope-filtered) lead-search requests. The local input value
 * updates synchronously on every keystroke so the field never feels laggy.
 */
const SEARCH_DEBOUNCE_MS = 500;

/**
 * WhatsApp support lines behind the headphones icon in the header. Each
 * opens a wa.me chat in a new tab (wa.me wants country code + number,
 * digits only). Owner's ask 2026-10-13: ADD +91 62385 33609 beside the
 * existing +91 90616 63675 line, so the icon became a two-entry menu
 * instead of a single link. Order = display order.
 */
const SUPPORT_WHATSAPP_LINES = [
  { digits: "919061663675", display: "+91 90616 63675" },
  { digits: "916238533609", display: "+91 62385 33609" },
] as const;
import {
  ArrowLeft,
  ArrowLeftRight,
  ChevronDown,
  Compass,
  Headphones,
  LogOut,
  Undo2,
  User,
} from "lucide-react";
import { Check } from "lucide-react";
import { useOnboardingTour } from "@/features/onboardingTour/useOnboardingTour";
import { Button } from "@/components/ui/button";
import SearchInput from "@/components/ui/searchInput";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/library/dropdown-menu";
import { WhatsAppIcon } from "@/utils/Icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/library/popover";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import { logout } from "@/stores/slices/authSlice";
import { setLeadSearch } from "@/features/leads/stores/leadFilterSlice";
import { setAutomationSearch } from "@/features/automation/stores/automationFunctionalSlice";
import useBranches from "@/features/users/api/useBranches";
import { useLeadFilterParams } from "@/features/leads/api/useLeadFilterParams";
import { useLeadsListCount } from "@/features/leads/api/useLeadsListCount";
import { useDynamicFilterParams } from "@/features/dynamic/api/useDynamicFilterParams";
import usePipeline from "@/features/stage/api/usePipeline";
import { getFromStage } from "@/utils/getFromStage";
import { useViewPinnedStageId } from "@/features/favoriteViews/utils/useViewPinnedStage";
import {
  setLeadFilterField,
  applyFilters as applyLeadFilters,
} from "@/features/leads/stores/leadFilterSlice";
import { selectCanBypassRbacChecks } from "@/rbac/rbacSlice";
import { useDynamicsRouteMeta } from "@/hooks/useDynamicsRouteMeta";
import {
  useLeadsPipelineBranchId,
  useStagePipelineBranchId,
} from "@/features/branchPipeline/sidebarPipelines";
import {
  selectDynamicFilters,
  setDynamicSearch,
  setDynamicFilterField,
  applyDynamicFilters,
} from "@/features/dynamic/store/dynamicFilterSlice";
import axiosInstance from "@/lib/axios";
import { resetQueryClientForLogout } from "@/lib/queryClient";
import { persistor } from "@/stores/store";
import { logoutEndpoit, switchBackPath } from "@/config/endpoints";
import { toast } from "sonner";
import { getInitials } from "@/utils/convetors";
import { usePageHeader } from "@/contexts/PageHeaderContext";
import NotificationDropdown from "@/features/notifications/components/NotificationDropdown";
import AlertDropdown from "@/features/alerts/components/AlertDropdown";
import AIToggleButton from "@/features/ai/components/AIToggleButton";
import ChatDropdown from "@/features/chat/components/ChatDropdown";
import SwitchUserDialog from "@/features/auth/impersonation/SwitchUserDialog";
import PresenceControl from "@/features/hr/attendance/components/PresenceControl";
import ImpersonationBanner, {
  useIsImpersonating,
} from "@/features/auth/impersonation/ImpersonationBanner";
import useSwitchUserCandidates from "@/features/auth/impersonation/useSwitchUserCandidates";
import { readActorSession } from "@/features/auth/impersonation/impersonationStorage";
import { switchBackToActor } from "@/features/auth/impersonation/impersonationActions";
import StageStatusPicker, {
  type StageStatusKey,
  SinglePickerPopover,
} from "@/components/layout/header/StageStatusPicker";
import { useCountryCustomField } from "@/components/layout/header/useCountryCustomField";
import { useKeyFields, type KeyField } from "@/features/customField/api/useKeyFields";
import { useFavoriteViews } from "@/features/favoriteViews/api/useFavoriteViews";
import { parseViewRouteId } from "@/features/favoriteViews/types";
import ReportHeaderTitle from "@/features/report/v2/ReportHeaderTitle";
import ReportViewShareButton from "@/features/report/v2/share/ReportViewShareButton";
import DashboardV2HeaderTitle from "@/features/Dashboard/v2/DashboardV2HeaderTitle";
import { dashboardV2Actions } from "@/features/Dashboard/v2/state/dashboardV2Slice";
import { useCountryLeadStats } from "@/features/leads/api/useCountryLeadStats";
import { usePipelineLeadStats } from "@/features/leads/api/usePipelineLeadStats";
import { LEAD_FILTER_INDUSTRY_IDS } from "@/features/leads/components/leadFilter/config/leadFilterIndustryConfig";

type BranchPickerVariant = "title" | "pill";

type BranchPickerSelection =
  | { kind: "all" }
  | { kind: "branch"; id: string }
  | { kind: "no_branch" }
  | { kind: "unassigned" };

interface BranchScopePickerProps {
  branchId: string;
  noBranch: boolean;
  assignmentStatus: string;
  canUseNoBranchFilter: boolean;
  onSelect: (next: BranchPickerSelection) => void;
  variant?: BranchPickerVariant;
  /**
   * Serialized active list filters that the per-branch / All-Leads / No-Branch /
   * Unassigned counts must "swing" with. Passed IN (not read off a fixed slice)
   * because this picker is shared by the /leads route AND every niche/Dynamics
   * route, which keep their filters in DIFFERENT redux slices — `LeadsBranchTitle`
   * passes `useLeadFilterParams()`, `DynamicsBranchScopePicker` passes
   * `useDynamicFilterParams(routeId)`. Defaults to no filters.
   */
  countFilters?: Record<string, any>;
  /**
   * Count to display in the TITLE, overriding the branches-endpoint
   * aggregate. `LeadsBranchTitle` passes the `/leads/count` total — the SAME
   * endpoint/params the list and its pagination use — so the header number
   * equals the list by construction. The aggregate pipeline mirrors the
   * list's filters facet-by-facet and drifted more than once (role/user were
   * stripped → an Overdue·Counsellor drill titled 3,996 over a list of 684);
   * the dropdown ROWS still ride the aggregate (they need per-branch splits),
   * but the headline number must never come from a parallel pipeline.
   */
  titleCount?: number;
  /**
   * Title shown for the default all-branches scope. Favorite View routes pass
   * the view's name so the header names the view, not "All Leads" — the
   * dropdown's own "All Leads" row keeps its label, since inside the list it
   * means the branch scope, not the route.
   */
  allLabel?: string;
}

function BranchScopePicker({
  branchId,
  noBranch,
  assignmentStatus,
  canUseNoBranchFilter,
  onSelect,
  variant = "title",
  countFilters,
  titleCount,
  allLabel = "All Leads",
}: BranchScopePickerProps) {
  // Branch-picker counts "swing" with the caller's active list filters: send
  // them under a NAMESPACED `leadCountFilters` param so the server narrows the
  // per-branch / All-Leads / No-Branch / Unassigned counts to the current
  // filter set (and excludes archived) WITHOUT colliding with the branches
  // endpoint's own name/code/city search. JSON-encoded so qs doesn't flatten
  // the nested filter object onto the branch-list filters. `countFilters` is
  // route-scoped by the wrapper (leads vs Dynamics slice), so this picker shows
  // counts for the list the user is actually looking at.
  const branchCountFilters = useMemo(
    () => ({ leadCountFilters: JSON.stringify(countFilters ?? {}) }),
    [countFilters],
  );
  const {
    branchSelects,
    aggregates,
    length: branchCount,
    isLoading,
  } = useBranches(undefined, branchCountFilters);

  const selectedBranchIds = useMemo(
    () => (branchId ? branchId.split(",").filter(Boolean) : []),
    [branchId],
  );

  // Header label reflects whatever filter is active so the user always
  // knows which slice they're looking at without opening the popover.
  const triggerLabel = useMemo(() => {
    if (noBranch) return "No Branch";
    if (assignmentStatus === "unassigned") return "Unassigned";
    if (selectedBranchIds.length === 1) {
      const b = branchSelects.find(
        (s: any) => String(s.value) === selectedBranchIds[0],
      );
      return b?.name ?? "Branch";
    }
    if (selectedBranchIds.length > 1) {
      return `${selectedBranchIds.length} Branches`;
    }
    return allLabel;
  }, [noBranch, assignmentStatus, selectedBranchIds, branchSelects, allLabel]);

  // Treat the dropdown as a single-select scope picker:
  //  - Items are mutually exclusive (picking one clears the others).
  //  - Clicking the currently-active item toggles back to "All Leads"
  //    (the default), so the user can dismiss a filter from the same
  //    place they set it.
  const isAllActive =
    !noBranch &&
    selectedBranchIds.length === 0 &&
    assignmentStatus !== "unassigned";
  const isNoBranchActive = noBranch;
  const isUnassignedActive = assignmentStatus === "unassigned";
  const isBranchActive = (id: string) =>
    !noBranch &&
    assignmentStatus !== "unassigned" &&
    selectedBranchIds.length === 1 &&
    selectedBranchIds[0] === id;

  const handlePickAll = () => onSelect({ kind: "all" });
  const handlePickBranch = (id: string) =>
    onSelect(isBranchActive(id) ? { kind: "all" } : { kind: "branch", id });
  const handlePickNoBranch = () =>
    onSelect(isNoBranchActive ? { kind: "all" } : { kind: "no_branch" });
  const handlePickUnassigned = () =>
    onSelect(isUnassignedActive ? { kind: "all" } : { kind: "unassigned" });

  // Hide the dropdown entirely for users who only have a single branch
  // and no admin override — there's nothing to switch between.
  const hasMultipleBranches = (branchCount ?? 0) > 1;
  const showDropdown = hasMultipleBranches || canUseNoBranchFilter;

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Single flat list of pickable options — branches plus the special
  // "All / No Branch / Unassigned" entries — so the search input can match
  // against them all uniformly. `branchSelects` arrives pre-sorted from
  // `useBranches`; the specials are pinned to the top/bottom by insertion
  // order when there's no active search query.
  type Kind = "all" | "branch" | "no_branch" | "unassigned";
  type Option = { value: string; name: string; kind: Kind; count?: number };

  // "All Leads" count comes from the server's `totalLeadCount` aggregate —
  // a direct `tenantDb.leads.count({ where: leadScope })` that excludes
  // trashed leads (via the soft-delete extension) AND includes leads with
  // `branchId = null`. Summing per-branch counts client-side would miss
  // the latter and include the former, both of which were bugs in the UI.
  const totalLeadCount = aggregates?.totalLeadCount ?? 0;
  // Headline number: the list's own count when supplied (equal to the list by
  // construction), else the aggregate. The dropdown's "All Leads" ROW keeps
  // the aggregate on purpose — it answers "what you'd get by switching to
  // All", which differs from the current list whenever a branch is picked.
  const headlineCount = titleCount ?? totalLeadCount;

  const allOptions = useMemo<Option[]>(() => {
    return [
      { value: "__all__", name: "All Leads", kind: "all", count: totalLeadCount },
      ...branchSelects.map((b: any) => ({
        value: String(b.value),
        name: String(b.name),
        kind: "branch" as const,
        count: Number(b.leadCount) || 0,
      })),
      ...(canUseNoBranchFilter
        ? [
            {
              value: "__no_branch__",
              name: "No Branch",
              kind: "no_branch" as const,
              count: aggregates?.noBranchLeadCount ?? 0,
            },
          ]
        : []),
      {
        value: "__unassigned__",
        name: "Unassigned",
        kind: "unassigned",
        count: aggregates?.unassignedLeadCount ?? 0,
      },
    ];
  }, [branchSelects, canUseNoBranchFilter, totalLeadCount, aggregates]);

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allOptions;
    return allOptions.filter((o) => o.name.toLowerCase().includes(q));
  }, [allOptions, search]);

  if (!showDropdown) {
    if (variant === "pill") return null;
    return (
      <div className="flex items-center gap-2 min-w-0 shrink">
        <h4 className="!text-[1rem] md:!text-[1.25rem] mainHeader-title truncate">
          {allLabel}
        </h4>
        {headlineCount > 0 && (
          <span
            className="text-b2 text-muted-foreground mainHeader-title-count shrink-0"
            style={{ marginTop: ".15rem" }}
          >
            ({headlineCount.toLocaleString()})
          </span>
        )}
      </div>
    );
  }

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setSearch("");
  };

  const isOptionChecked = (o: Option) => {
    switch (o.kind) {
      case "all":
        return isAllActive;
      case "branch":
        return isBranchActive(o.value);
      case "no_branch":
        return isNoBranchActive;
      case "unassigned":
        return isUnassignedActive;
    }
  };

  const handlePick = (o: Option) => {
    switch (o.kind) {
      case "all":
        handlePickAll();
        break;
      case "branch":
        handlePickBranch(o.value);
        break;
      case "no_branch":
        handlePickNoBranch();
        break;
      case "unassigned":
        handlePickUnassigned();
        break;
    }
    handleOpenChange(false);
  };

  const triggerClass =
    variant === "pill"
      ? "inline-flex items-center gap-1.5 h-8 shrink-0 rounded-lg border border-[#e6e8e7] bg-[#f7f9f8] px-2.5 hover:bg-[#eef1f0] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-success)]/30 transition-colors cursor-pointer disabled:opacity-60"
      : "flex items-center gap-1.5 min-w-0 shrink rounded-md px-1 -mx-1 hover:bg-[var(--color-black-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-success)]/30 transition-colors cursor-pointer";
  const labelClass =
    variant === "pill"
      ? "text-[12px] font-medium tracking-[-0.24px] text-[#374741] truncate max-w-[140px]"
      : "!text-[1rem] md:!text-[1.25rem] mainHeader-title truncate mb-0";
  const countClass =
    variant === "pill"
      ? "text-[11px] text-[#9ba3a0] tabular-nums shrink-0"
      : "text-b2 text-muted-foreground mainHeader-title-count shrink-0";

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button type="button" disabled={isLoading} className={triggerClass}>
          {variant === "pill" ? (
            <span className={labelClass}>{triggerLabel}</span>
          ) : (
            <h4 className={labelClass}>{triggerLabel}</h4>
          )}
          {triggerLabel === allLabel && headlineCount > 0 && (
            <span
              className={countClass}
              style={variant === "title" ? { marginTop: ".15rem" } : undefined}
            >
              ({headlineCount.toLocaleString()})
            </span>
          )}
          <ChevronDown
            className="size-4 text-[var(--color-text-secondary)] shrink-0"
            strokeWidth={1.75}
          />
        </button>
      </PopoverTrigger>
      {/*
        Mirrors `MultiSelectContent`: PopoverContent is the scroll
        container, search header is `sticky top-0`. See multi-select.tsx
        for the full reasoning — short version is that flex-1 + min-h-0
        body inside Radix's animated popover sometimes doesn't claim
        available height, so we put the overflow on the root and use a
        sticky header to keep search pinned.
      */}
      <PopoverContent
        align="start"
        sideOffset={8}
        className="p-0 w-[260px] max-w-[18rem] overscroll-contain"
        // Tall enough for ~16 branches before it scrolls, but never past the
        // space Radix measured under the trigger (small laptop screens).
        style={{
          maxHeight:
            "min(var(--radix-popover-content-available-height, 80vh), 80vh, 40rem)",
          overflowY: "auto",
        }}
      >
        <div className="sticky top-0 z-10 border-b border-[var(--color-black-2)] bg-popover p-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search branches..."
            className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[2px]"
            autoFocus
          />
        </div>

        <div className="p-1">
          {filteredOptions.length === 0 ? (
            <p className="py-6 text-center text-sm text-[var(--color-text-gray)]">
              No matches for &ldquo;{search}&rdquo;
            </p>
          ) : (
            filteredOptions.map((o) => (
              <BranchOptionRow
                key={o.value}
                label={o.name}
                count={o.count}
                checked={isOptionChecked(o)}
                onSelect={() => handlePick(o)}
              />
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Translates a generic {@link BranchPickerSelection} into the
 * `branchId` / `noBranch` / `assignmentStatus` triple every consumer
 * slice (leads / dynamic / future filter slices) shares. Reuse this in
 * any new wrapper so the picker stays a single source of truth — the
 * Leads and Dynamics wrappers below only differ in where they dispatch.
 */
function applyBranchPickerSelection(
  sel: BranchPickerSelection,
  dispatchField: (key: "branchId" | "noBranch" | "assignmentStatus", value: any) => void,
  commit: () => void,
) {
  dispatchField("branchId", "");
  dispatchField("noBranch", false);
  dispatchField("assignmentStatus", "all");
  if (sel.kind === "branch") dispatchField("branchId", sel.id);
  else if (sel.kind === "no_branch") dispatchField("noBranch", true);
  else if (sel.kind === "unassigned") dispatchField("assignmentStatus", "unassigned");
  commit();
}

function LeadsBranchTitle() {
  const dispatch = useAppDispatch();
  const canUseNoBranchFilter = useAppSelector(selectCanBypassRbacChecks);
  const branchId = useAppSelector((s) => s.leadFilterSlice.branchId);
  const noBranch = useAppSelector((s) => s.leadFilterSlice.noBranch);
  const assignmentStatus = useAppSelector(
    (s) => s.leadFilterSlice.assignmentStatus,
  );
  const countFilters = useLeadFilterParams();
  // The title's number = the list's own filtered total (/leads/count). Same
  // hook + params as LeadsPage's pagination/Select-All count, so React Query
  // dedupes the request and the header can never disagree with the list.
  const { total: filteredTotal } = useLeadsListCount(countFilters);
  return (
    <BranchScopePicker
      branchId={branchId}
      noBranch={noBranch}
      assignmentStatus={assignmentStatus}
      canUseNoBranchFilter={canUseNoBranchFilter}
      countFilters={countFilters}
      titleCount={filteredTotal}
      onSelect={(sel) =>
        applyBranchPickerSelection(
          sel,
          (key, value) => dispatch(setLeadFilterField({ key, value })),
          () => dispatch(applyLeadFilters()),
        )
      }
      variant="title"
    />
  );
}

/**
 * Count params for the header pickers on a DYNAMIC route. Dynamic stage
 * routes are STAGE-scoped lists, so every picker's counts must be
 * stage-scoped too — without `stage` the server counts pipeline-wide, and an
 * option whose leads sit in other stages shows a non-zero count over an empty
 * list. `stage` runs through the same child-aware stage match as the
 * stage-route list (pipeline() + applyChildAwareStageFilter).
 *
 * The date semantics mirror `getStageLeads` exactly: on a stage route a date
 * range means "entered THIS stage in the window" (target pinned to the route
 * stage), and once a Status / Sub Status chip is picked the server defaults
 * the clock to that chip's own change (`resolveEntryLevelName`) — the pin is
 * what tells it the route stage is the target. ONE hook for branch, country
 * and status counts so all three ride the list's clock; before this the
 * country picker sent no stage at all and the status picker sent no target,
 * so their numbers could disagree with the list they narrow. A saved-view
 * route has no stage and passes its params through untouched.
 */
function useStageRouteCountFilters(routeId: string | number) {
  const routeFilters = useDynamicFilterParams(routeId);
  // A branch pipeline's stage is looked up in that pipeline (undefined → the
  // default pipeline, as before).
  const { pipeline } = usePipeline(undefined, useStagePipelineBranchId(routeId));
  // A saved view pinned to a stage has no stage in the pipeline to resolve
  // from its routeId — it carries the stage in its params instead (see
  // `useDynamicFilterParams`), and must ride the same clock as the stage tab
  // it was saved from or its picker counts disagree with its own list.
  const routeStage = getFromStage({ stagePipeline: pipeline, id: routeId })?.id;
  const pinnedStage =
    Number.isFinite(Number(routeFilters.stageId)) && Number(routeFilters.stageId) > 0
      ? Number(routeFilters.stageId)
      : undefined;
  const stageId = routeStage ?? pinnedStage;
  return useMemo(() => {
    if (!stageId) return routeFilters;
    // Inert while no date filter is active; an explicit user override wins.
    const dateType = routeFilters.dateType || "statusChangedAt";
    return {
      stage: stageId,
      // `stageId` = "this request IS that stage's list" (`resolveListContext`
      // on the server): the branch / country / status counters then ride the
      // stage route's OWN chain (stage-scoped Active/Inactive, stage-scoped
      // anchor, the route clock) and each number equals the list it opens by
      // construction. `stage` stays: it is the status picker's pipeline pin
      // and what an older server reads.
      stageId,
      dateType,
      ...(dateType === "statusChangedAt"
        ? { stageChangedToStageId: stageId }
        : {}),
      ...routeFilters,
    };
  }, [stageId, routeFilters]);
}

function DynamicsBranchScopePicker({
  routeId,
  allLabel,
}: {
  routeId: string | number;
  allLabel?: string;
}) {
  const dispatch = useAppDispatch();
  const canUseNoBranchFilter = useAppSelector(selectCanBypassRbacChecks);
  const { branchId, noBranch, assignmentStatus } = useAppSelector((s) =>
    selectDynamicFilters(s, routeId),
  );
  const countFilters = useStageRouteCountFilters(routeId);
  return (
    <BranchScopePicker
      branchId={branchId}
      noBranch={noBranch}
      assignmentStatus={assignmentStatus}
      canUseNoBranchFilter={canUseNoBranchFilter}
      countFilters={countFilters}
      allLabel={allLabel}
      onSelect={(sel) =>
        applyBranchPickerSelection(
          sel,
          (key, value) =>
            dispatch(setDynamicFilterField({ routeId, key, value })),
          () => dispatch(applyDynamicFilters(routeId)),
        )
      }
      variant="title"
    />
  );
}

/**
 * Branch title for a Favorite View route — same picker, but the default
 * all-branches scope is titled with the VIEW's name ("Document Pending"), not
 * "All Leads", so the header names the slice the sidebar navigated to.
 */
/** Share links: the snapshot's label, read from the page's query cache. */
function SharedViewHeaderTitle({ routeId }: { routeId: string | number }) {
  const token = parseSharedRouteId(routeId) ?? "";
  const { data } = useSharedView(token || undefined);
  return (
    <DynamicsBranchScopePicker
      routeId={routeId}
      allLabel={data?.label ? `Shared · ${data.label}` : "Shared view"}
    />
  );
}

function FavoriteViewBranchTitle({ routeId }: { routeId: string | number }) {
  const viewId = parseViewRouteId(routeId);
  const { views } = useFavoriteViews();
  const view = views.find((v) => v.id === viewId);
  return (
    <DynamicsBranchScopePicker routeId={routeId} allLabel={view?.name} />
  );
}

/**
 * Wires StageStatusPicker to `leadFilterSlice` for the /leads route — all
 * three dropdowns are shown because no stage is implied by the URL.
 * Field changes are dispatched then committed with `applyLeadFilters()` so
 * the leads list re-fetches immediately, mirroring the BranchScopePicker
 * pattern in `LeadsBranchTitle`.
 */
function LeadsStageStatusPicker() {
  const dispatch = useAppDispatch();
  const stage = useAppSelector((s) => s.leadFilterSlice.stage);
  const status = useAppSelector((s) => s.leadFilterSlice.status);
  const subStatus = useAppSelector((s) => s.leadFilterSlice.subStatus);
  // Filter-aware counts that swing with the active list filters (incl. the
  // current stage/status, which the server uses to scope the status/sub counts).
  const counts = usePipelineLeadStats(useLeadFilterParams());
  // One branch picked that runs its own pipeline → its stages; nothing picked
  // and the user's branches all run one branch pipeline → that one; else default.
  const pipelineBranchId = useLeadsPipelineBranchId();
  const dateType = useAppSelector((s) => s.leadFilterSlice.dateType);
  const stageChangedTo = useAppSelector(
    (s) => s.leadFilterSlice.stageChangedTo
  ) as string;
  const entryLevel = useAppSelector(
    (s) => s.leadFilterSlice.entryLevel
  ) as string;
  return (
    <StageStatusPicker
      value={{ stage, status, subStatus }}
      counts={counts}
      pipelineBranchId={pipelineBranchId}
      onSelect={({ key, value }) => {
        dispatch(setLeadFilterField({ key, value }));
        // Editing the stage, status or sub-status by hand returns matching to
        // the default child-aware grain — the lead-grain marker belongs to the
        // report drill ↗ cell that set it, not to whatever is picked next (a
        // status edit used to keep it: same chips, 41 vs 72).
        dispatch(setLeadFilterField({ key: "stageGrain", value: "" }));
        // Stage-ENTRY semantics ("moved into X in the window") are armed from
        // the filter drawer, which ASKS first. This quick picker doesn't ask —
        // so it must not leave a target armed for a stage the user just
        // navigated away from, which would silently intersect two different
        // stages and return nothing. Prune the target to the new selection;
        // if none survives, fall back to Created At.
        if (key === "stage" && stageChangedTo) {
          const picked = String(value ?? "")
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean);
          const kept = stageChangedTo
            .split(",")
            .map((v) => v.trim())
            .filter((id) => id && picked.includes(id));
          dispatch(
            setLeadFilterField({
              key: "stageChangedTo",
              value: kept.join(","),
            })
          );
          if (kept.length === 0 && dateType === "statusChangedAt") {
            dispatch(setLeadFilterField({ key: "dateType", value: "createdAt" }));
            dispatch(setLeadFilterField({ key: "entryLevel", value: "" }));
          }
        }
        // The entry level ("count status / sub-status changes") points at the
        // Status / Sub Status chips; clearing those returns the date range to
        // stage-level entry rather than leaving a clock on nothing.
        if (key === "status" && !String(value ?? "").trim() && entryLevel) {
          dispatch(setLeadFilterField({ key: "entryLevel", value: "" }));
        }
        if (
          key === "subStatus" &&
          !String(value ?? "").trim() &&
          entryLevel === "subStatus"
        ) {
          dispatch(setLeadFilterField({ key: "entryLevel", value: "status" }));
        }
        dispatch(applyLeadFilters());
      }}
    />
  );
}

/**
 * Orders the "Pick country" rows (and every key field's "Pick team" rows) by
 * their filter-aware lead count (highest first) instead of the field's own
 * option order, so the destinations that actually hold leads sit at the top
 * and the dead ones sink.
 * Ties break alphabetically; while the counts are still loading the field's
 * order is kept as-is. The "No country" sentinel is appended by the callers
 * AFTER this sort — it is not a destination, so it stays pinned last even when
 * the unset bucket is large.
 */
function orderCountryOptionsByCount(
  options: Array<{ id: number | string; name: string }>,
  counts: Record<string, number> | undefined
) {
  if (!counts) return options;
  return [...options].sort((a, b) => {
    const diff = (counts[String(b.id)] ?? 0) - (counts[String(a.id)] ?? 0);
    return diff !== 0 ? diff : a.name.localeCompare(b.name);
  });
}

/** The tenant's Country field as the header pickers see it: only Study
 *  Abroad / Education tenants have one (`field` is null everywhere else). */
function useHeaderCountryField() {
  const industryId = useAppSelector(
    (s) => s.auth?.currentUser?.user?.tenant?.industryId
  );
  const isStudyAbroad =
    industryId === LEAD_FILTER_INDUSTRY_IDS.STUDY_ABROAD ||
    industryId === LEAD_FILTER_INDUSTRY_IDS.EDUCATION;
  const { field } = useCountryCustomField(isStudyAbroad);
  return { isStudyAbroad, field: isStudyAbroad ? field : null };
}

/**
 * The header KEY FIELDS (e.g. an academy's "Team") — every one the admin
 * ticked, in any industry, minus a field the Country picker already renders.
 */
function useHeaderKeyFields(): KeyField[] {
  const { fields } = useKeyFields();
  const { field: countryField } = useHeaderCountryField();
  return useMemo(
    () => fields.filter((f) => f.id !== countryField?.id),
    [fields, countryField?.id]
  );
}

/**
 * ONE quick single-pick dropdown for a dropdown custom field — the header's
 * "Pick country", and the same picker for every KEY FIELD ("Pick team").
 * Selecting an option writes `customFieldFilters[<fieldId>]` (`cf_<id>`), the
 * filter the expanded panel uses, so header and panel stay in sync; the
 * caller says WHERE that object lives (the leads slice or a route's slice).
 *
 * Per-option counts come from `/core/leads/country-stats` with
 * `countryFieldId=<this field>` — generic over any field: the server strips
 * this field's own facet so every option keeps its count, and counts each
 * with the EXACT list `where`, so a row's number equals its click-through.
 * Copy is the field's own ("Country", "Team"), never Country's on a team.
 */
function CustomFieldQuickPicker({
  field,
  singular,
  plural,
  customFieldFilters,
  onChange,
  countFilters,
}: {
  field: { id: number; options: { id: number; value: string }[] };
  /** "Country" | "Team" */
  singular: string;
  /** "Countries" | "Teams" */
  plural: string;
  customFieldFilters: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  /** The active list filters the counts swing with. */
  countFilters: Record<string, unknown>;
}) {
  const { counts, total, noCountry } = useCountryLeadStats(field.id, countFilters);

  const fieldId = String(field.id);
  // Header is single-select; if the panel set multiple values for this field
  // (comma-joined) we just show the placeholder rather than a partial match.
  const selectedId = customFieldFilters[fieldId] ?? "";

  const handlePick = (optionId: string) => {
    const next: Record<string, string> = { ...customFieldFilters };
    if (optionId) next[fieldId] = optionId;
    else delete next[fieldId];
    onChange(next);
  };

  const selectedOption = field.options.find(
    (o) => String(o.id) === selectedId
  );
  const noun = singular.toLowerCase();
  const nounPlural = plural.toLowerCase();

  // Append a "No country" / "No team" row (the `none` sentinel) so leads
  // with no value are visible. Every row's count equals its click-through
  // list (multi-country leads sit in several buckets); only under Verified is
  // it a partition.
  const options = [
    ...orderCountryOptionsByCount(
      field.options.map((o) => ({ id: o.id as number | string, name: o.value })),
      counts
    ),
    { id: "none" as const, name: `No ${noun}` },
  ];
  const countsWithNone = counts ? { ...counts, none: noCountry } : undefined;

  return (
    <SinglePickerPopover
      triggerLabel={
        selectedOption
          ? `${singular}: ${selectedOption.value}`
          : selectedId === "none"
            ? `${singular}: No ${noun}`
            : null
      }
      placeholder={`Pick ${noun}`}
      options={options}
      selectedId={selectedId}
      onPick={handlePick}
      searchPlaceholder={`Search ${nounPlural}…`}
      emptyHint={`No matching ${nounPlural}`}
      counts={countsWithNone}
      totalCount={total}
    />
  );
}

/**
 * Quick "Pick country" dropdown for the leads header, shown only for
 * Study Abroad / Education tenants that have a `Country`-type custom field
 * (the lead's preferred destination country). Selecting a country drives the
 * same `customFieldFilters` (`cf_<id>`) the expanded filter panel uses, so the
 * header and panel stay in sync. Renders nothing for other industries or when
 * no Country field exists. Placed before the Stage picker in the header.
 */
function LeadsCountryPicker() {
  const { field } = useHeaderCountryField();
  if (!field) return null;
  return (
    <LeadsCustomFieldPicker
      field={field}
      singular="Country"
      plural="Countries"
    />
  );
}

/** "Pick team" — one per KEY FIELD, beside "Pick country", any industry. */
function LeadsKeyFieldPickers() {
  const keyFields = useHeaderKeyFields();
  return (
    <>
      {keyFields.map((f) => (
        <LeadsCustomFieldPicker
          key={f.id}
          field={f}
          singular={f.name}
          plural={f.plural}
        />
      ))}
    </>
  );
}

/** {@link CustomFieldQuickPicker} on the /leads filter slice. */
function LeadsCustomFieldPicker({
  field,
  singular,
  plural,
}: {
  field: { id: number; options: { id: number; value: string }[] };
  singular: string;
  plural: string;
}) {
  const dispatch = useAppDispatch();
  const customFieldFilters = useAppSelector(
    (s) => s.leadFilterSlice.customFieldFilters
  ) as Record<string, string>;
  // Per-option counts that swing with the active leads-list filters — the
  // same params the list/branch picker uses.
  const countFilters = useLeadFilterParams();
  return (
    <CustomFieldQuickPicker
      field={field}
      singular={singular}
      plural={plural}
      customFieldFilters={customFieldFilters}
      countFilters={countFilters}
      onChange={(next) => {
        dispatch(setLeadFilterField({ key: "customFieldFilters", value: next }));
        dispatch(applyLeadFilters());
      }}
    />
  );
}

/**
 * Wires StageStatusPicker to `dynamicFilterSlice` for a stage-pinned route.
 * The Stage dropdown is hidden — `forcedStageId` (= the route's stageId)
 * pins the Status options. Status / SubStatus changes flow into the
 * route-scoped filter slice and trigger `applyDynamicFilters(routeId)`.
 */
function DynamicsStageStatusPicker({
  routeId,
  stageId,
}: {
  routeId: string | number;
  /**
   * The stage to pin the Status options to. Defaults to `routeId`, which IS
   * the stage id on a stage tab; a saved view's routeId is `view-<n>`, so it
   * passes its pinned stage explicitly.
   */
  stageId?: string | number;
}) {
  const dispatch = useAppDispatch();
  const { status, subStatus } = useAppSelector((s) =>
    selectDynamicFilters(s, routeId),
  );
  // The stage is pinned by the route — the shared count hook folds it in
  // (plus the route's entry target) so the status / sub counts ride the
  // same clock as the list they open.
  const counts = usePipelineLeadStats(useStageRouteCountFilters(routeId));
  // A branch pipeline's stage takes its statuses from that pipeline.
  const pipelineBranchId = useStagePipelineBranchId(stageId ?? routeId);
  return (
    <StageStatusPicker
      value={{ stage: "", status, subStatus }}
      hideStagePicker
      forcedStageId={stageId ?? routeId}
      counts={counts}
      pipelineBranchId={pipelineBranchId}
      onSelect={({ key, value }) => {
        if (key === "stage") return; // hidden on this route
        dispatch(
          setDynamicFilterField({
            routeId,
            key: key as Exclude<StageStatusKey, "stage">,
            value,
          }),
        );
        // A hand edit retires a report drill's grain marker here too (a saved
        // view pinned to a stage can carry one).
        dispatch(setDynamicFilterField({ routeId, key: "stageGrain", value: "" }));
        dispatch(applyDynamicFilters(routeId));
      }}
    />
  );
}

/**
 * Wires StageStatusPicker to `dynamicFilterSlice` for a Favorite View route.
 * Unlike a stage tab, a view pins no stage — all three dropdowns show, exactly
 * like the /leads header, but reads/writes go to the view's per-route slice so
 * they count as live narrowing on top of the saved filters.
 */
function FavoriteViewStageStatusPicker({
  routeId,
}: {
  routeId: string | number;
}) {
  const dispatch = useAppDispatch();
  const { stage, status, subStatus, dateType, stageChangedTo, entryLevel } =
    useAppSelector((s) => selectDynamicFilters(s, routeId));
  const counts = usePipelineLeadStats(useDynamicFilterParams(routeId));
  return (
    <StageStatusPicker
      value={{ stage, status, subStatus }}
      counts={counts}
      onSelect={({ key, value }) => {
        dispatch(setDynamicFilterField({ routeId, key, value }));
        // Same guards as LeadsStageStatusPicker: a manual stage / status /
        // sub-status pick returns matching to the child-aware grain, and a
        // stage pick prunes any armed stage-entry target so two different
        // stages never silently intersect.
        dispatch(
          setDynamicFilterField({ routeId, key: "stageGrain", value: "" }),
        );
        if (key === "stage" && stageChangedTo) {
          const picked = String(value ?? "")
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean);
          const kept = String(stageChangedTo)
            .split(",")
            .map((v) => v.trim())
            .filter((id) => id && picked.includes(id));
          dispatch(
            setDynamicFilterField({
              routeId,
              key: "stageChangedTo",
              value: kept.join(","),
            }),
          );
          if (kept.length === 0 && dateType === "statusChangedAt") {
            dispatch(
              setDynamicFilterField({
                routeId,
                key: "dateType",
                value: "createdAt",
              }),
            );
            dispatch(
              setDynamicFilterField({ routeId, key: "entryLevel", value: "" }),
            );
          }
        }
        // Same as LeadsStageStatusPicker: the entry level points at the
        // Status / Sub Status chips, so clearing those steps it back.
        if (key === "status" && !String(value ?? "").trim() && entryLevel) {
          dispatch(
            setDynamicFilterField({ routeId, key: "entryLevel", value: "" }),
          );
        }
        if (
          key === "subStatus" &&
          !String(value ?? "").trim() &&
          entryLevel === "subStatus"
        ) {
          dispatch(
            setDynamicFilterField({
              routeId,
              key: "entryLevel",
              value: "status",
            }),
          );
        }
        dispatch(applyDynamicFilters(routeId));
      }}
    />
  );
}

/**
 * Route-scoped "Pick country" dropdown for dynamic tabs — the dynamic-tab
 * twin of {@link LeadsCountryPicker}. Shown only for Study Abroad / Education
 * tenants that have a `Country`-type custom field. Selecting a country drives
 * the route's `customFieldFilters` (`cf_<id>`), serialised the same way as the
 * leads list (`serializeFilterParams`), so the dynamic list filters correctly.
 * Renders nothing for other industries or when no Country field exists. Placed
 * before the Stage/Status picker in the dynamic-tab header.
 */
function DynamicsCountryPicker({ routeId }: { routeId: string | number }) {
  const { field } = useHeaderCountryField();
  if (!field) return null;
  return (
    <DynamicsCustomFieldPicker
      routeId={routeId}
      field={field}
      singular="Country"
      plural="Countries"
    />
  );
}

/** "Pick team" on a dynamic tab / favourite / shared view — one per key field. */
function DynamicsKeyFieldPickers({ routeId }: { routeId: string | number }) {
  const keyFields = useHeaderKeyFields();
  return (
    <>
      {keyFields.map((f) => (
        <DynamicsCustomFieldPicker
          key={f.id}
          routeId={routeId}
          field={f}
          singular={f.name}
          plural={f.plural}
        />
      ))}
    </>
  );
}

/** {@link CustomFieldQuickPicker} on a route's own filter slice. */
function DynamicsCustomFieldPicker({
  routeId,
  field,
  singular,
  plural,
}: {
  routeId: string | number;
  field: { id: number; options: { id: number; value: string }[] };
  singular: string;
  plural: string;
}) {
  const dispatch = useAppDispatch();
  const { customFieldFilters } = useAppSelector((s) =>
    selectDynamicFilters(s, routeId)
  );
  // Per-option counts that swing with this dynamic tab's active filters.
  // Stage-pinned like the other pickers so each count equals the list.
  const countFilters = useStageRouteCountFilters(routeId);
  return (
    <CustomFieldQuickPicker
      field={field}
      singular={singular}
      plural={plural}
      customFieldFilters={(customFieldFilters ?? {}) as Record<string, string>}
      countFilters={countFilters}
      onChange={(next) => {
        dispatch(
          setDynamicFilterField({ routeId, key: "customFieldFilters", value: next })
        );
        dispatch(applyDynamicFilters(routeId));
      }}
    />
  );
}

function BranchOptionRow({
  label,
  count,
  checked,
  onSelect,
}: {
  label: string;
  count?: number;
  checked: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-2 text-left text-[13px] cursor-pointer hover:bg-[var(--color-black-1)] focus-visible:bg-[var(--color-black-1)] focus-visible:outline-none transition-colors"
    >
      <span className="truncate flex-1">{label}</span>
      <span className="flex items-center gap-2 shrink-0">
        {count !== undefined && (
          <span className="text-muted-foreground tabular-nums text-[12px]">
            {count.toLocaleString()}
          </span>
        )}
        {checked && (
          <Check
            className="size-4 text-[var(--color-success)]"
            strokeWidth={2}
          />
        )}
      </span>
    </button>
  );
}

// Exported for the full-screen list overlay (ListFullscreenShell), which keeps
// the search box while hiding the rest of the header.
export function LeadsHeaderSearch() {
  const dispatch = useAppDispatch();
  const leadSearch = useAppSelector((state) => state.leadFilterSlice.search);

  // Mirror the redux search in local state so the input itself updates
  // on every keystroke (no controlled-input lag), then push the value
  // out to redux + API only after the user pauses typing.
  const [localValue, setLocalValue] = useState(leadSearch);
  const debouncedValue = useDebounce(localValue, SEARCH_DEBOUNCE_MS);
  // The last value this instance agreed on with redux — either pulled down
  // from it or pushed up to it. See the push effect for why a ref, not
  // `leadSearch` itself, is the comparison target.
  const syncedRef = useRef(leadSearch);

  // If redux changes from outside (e.g. another action resets search),
  // pull the new value down into the input.
  useEffect(() => {
    syncedRef.current = leadSearch;
    setLocalValue(leadSearch);
  }, [leadSearch]);

  // Push debounced typing up to redux — ONLY when the debounced value moves,
  // never in reaction to redux moving.
  //
  // TWO INSTANCES CAN BE MOUNTED AT ONCE (the page header's box under the
  // full-screen list overlay + the overlay's own box), both bound to the same
  // redux key. A push effect that also depended on the redux value looped:
  // instance A pushes "x"; B's pull-down effect queues local="x", but B's
  // push effect runs in the SAME flush with its old closure (local "" and
  // debounced ""), sees "" !== "x" and pushes "" back; A then pushes "x"
  // again … until React threw "Maximum update depth exceeded" (#185).
  // Comparing against `localValue` did not help — inside that flush the two
  // were still equal. Depending on the debounced value alone, and comparing
  // it with the value this instance last synced (the ref, updated by the
  // pull-down effect before the timer fires), makes a redux change never
  // trigger a push, so a stale instance can't overwrite the live one.
  useEffect(() => {
    if (debouncedValue === syncedRef.current) return;
    syncedRef.current = debouncedValue;
    dispatch(setLeadSearch(debouncedValue));
  }, [debouncedValue, dispatch]);

  return (
    <SearchInput
      className="min-w-0"
      isKbd={false}
      expandOnFocus
      collapsedWidth="13rem"
      collapsedWidthMd="15rem"
      expandedWidth="17rem"
      expandedWidthMd="23rem"
      placeholder="Search name, phone, any field…"
      bgColor="white"
      value={localValue}
      onChange={(e: ChangeEvent<HTMLInputElement>) =>
        setLocalValue(e.target.value)
      }
    />
  );
}

export function DynamicsHeaderSearch({
  routeId,
  compact = false,
}: {
  routeId: string | number;
  /** Favorite View routes: the view NAME sits in the title slot and can be
   * long, so the search gives up width to keep the header on one line. */
  compact?: boolean;
}) {
  const dispatch = useAppDispatch();
  const search = useAppSelector(
    (state) => selectDynamicFilters(state, routeId).search
  );

  // Same debounce pattern as LeadsHeaderSearch — keep input snappy
  // locally, throttle the API hit to a single request after the user
  // pauses typing. See `SEARCH_DEBOUNCE_MS` for rationale.
  const [localValue, setLocalValue] = useState(search);
  const debouncedValue = useDebounce(localValue, SEARCH_DEBOUNCE_MS);
  const syncedRef = useRef(search);

  // Sync down: external resets / route changes update the input.
  useEffect(() => {
    syncedRef.current = search;
    setLocalValue(search);
  }, [search, routeId]);

  // Push ONLY when the debounced value moves — see LeadsHeaderSearch for the
  // two-instance loop this shape prevents (header box under the full-screen
  // overlay + the overlay's own box on the same redux key).
  useEffect(() => {
    if (debouncedValue === syncedRef.current) return;
    syncedRef.current = debouncedValue;
    dispatch(setDynamicSearch({ routeId, search: debouncedValue }));
  }, [debouncedValue, routeId, dispatch]);

  return (
    <SearchInput
      className={
        compact
          ? "w-full max-w-[12rem] md:max-w-[15rem] min-w-0"
          : "w-full max-w-[17rem] md:max-w-[23rem] min-w-0"
      }
      isKbd={false}
      placeholder="Search by name or company..."
      bgColor="white"
      value={localValue}
      onChange={(e: ChangeEvent<HTMLInputElement>) =>
        setLocalValue(e.target.value)
      }
    />
  );
}

/**
 * Dashboard: finds a WIDGET as you type — the grid shows only the cards whose
 * title holds every word (DashboardGrid; owner, 2026-10-22: "searchable any
 * widget just like leads"). Return jumps to the first match; with no card
 * matching, Return is the old lead quick-search: the Leads page opens with
 * the query applied. The search is cleared when you leave the dashboard — a
 * filter you can't see is a trap.
 */
function DashboardHeaderSearch() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const value = useAppSelector((state) => state.dashboardV2.widgetSearch);

  useEffect(
    () => () => {
      dispatch(dashboardV2Actions.setWidgetSearch(""));
    },
    [dispatch]
  );

  const onEnter = () => {
    const query = value.trim();
    if (!query) return;
    const firstMatch = document.querySelector<HTMLElement>(
      '[data-dash-cell][data-dash-match="1"]'
    );
    if (firstMatch) {
      firstMatch.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    dispatch(setLeadSearch(query));
    navigate("/leads");
  };

  return (
    <SearchInput
      className="w-full max-w-[17rem] md:max-w-[23rem] min-w-0"
      isKbd={false}
      placeholder="Search widgets — Enter searches leads"
      bgColor="white"
      value={value}
      onChange={(e: ChangeEvent<HTMLInputElement>) =>
        dispatch(dashboardV2Actions.setWidgetSearch(e.target.value))
      }
      onEnter={onEnter}
    />
  );
}

/**
 * /automation: the header box filters the My Automations cards by name,
 * description or group. The whole list is already loaded (server limit 200),
 * so filtering is client-side and needs no debounce.
 */
function AutomationHeaderSearch() {
  const dispatch = useAppDispatch();
  const search = useAppSelector(
    (state) => state.automationFunctional.automationSearch
  );

  // Leaving the page clears the query so it is not silently still applied
  // on the next visit.
  useEffect(() => {
    return () => {
      dispatch(setAutomationSearch(""));
    };
  }, [dispatch]);

  return (
    <SearchInput
      className="w-full max-w-[17rem] md:max-w-[23rem] min-w-0"
      isKbd={false}
      placeholder="Search automations..."
      bgColor="white"
      value={search}
      onChange={(e: ChangeEvent<HTMLInputElement>) =>
        dispatch(setAutomationSearch(e.target.value))
      }
    />
  );
}

export default function Header() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.currentUser?.user);
  const { startTour } = useOnboardingTour();
  // "Switch user" (view-as). A switch reloads the page, so `impersonating`
  // is fixed for the life of this mount. The candidate list is one small
  // request per session; the menu item shows only when it is non-empty.
  const impersonating = useIsImpersonating();
  const actorSession = useMemo(
    () => (impersonating ? readActorSession() : null),
    [impersonating],
  );
  const [switchUserOpen, setSwitchUserOpen] = useState(false);
  const { data: switchCandidates } = useSwitchUserCandidates(
    !impersonating && !!user?.id,
  );
  const canSwitchUser =
    !impersonating && (switchCandidates?.length ?? 0) > 0;
  const switchBackLabel = actorSession?.user.name
    ? `Switch back to ${actorSession.user.name}`
    : "Switch back to my account";
  const { headerInfo } = usePageHeader();
  const isLeadsRoute = pathname === "/leads";
  const isDashboardRoute = pathname === "/" || pathname === "";
  const isReportRoute = pathname.startsWith("/report");
  const isAutomationRoute = pathname.startsWith("/automation");
  const {
    isDynamicsRoute,
    isFavoriteViewRoute,
    isSharedViewRoute,
    routeId: dynamicsRouteId,
  } = useDynamicsRouteMeta();
  // Non-null when this route is a saved view that pins a pipeline stage.
  const viewPinnedStageId = useViewPinnedStageId(dynamicsRouteId);

  const handleLogout = () => {
    // Token at click time — used below to detect a NEW sign-in that landed
    // while the async teardown was still in flight.
    const outgoingToken = localStorage.getItem("xale_access_token");

    // Navigate immediately so the click feels instant — the network
    // round-trip and cache teardown should never block the UI. Protected
    // routes unmount on this navigate, which means later `dispatch(logout())`
    // is safe (Sidebar/Header/widgets that destructure `currentUser.user`
    // are gone before Redux flips to null).
    navigate("/login");
    toast.success("Logged out");

    // Background: server logout + client teardown. The auth token lives in
    // localStorage and is only cleared by `dispatch(logout())`, so the POST
    // must run before that dispatch — the order below preserves it.
    void (async () => {
      if (impersonating) {
        // Close the view-as audit trail before the sign-out itself; the
        // `logout` reducer below drops the parked session, ending both.
        try {
          await axiosInstance.post(switchBackPath);
        } catch {
          /* audit only */
        }
      }
      try {
        await axiosInstance.post(logoutEndpoit);
      } catch (e) {
        console.log(e, "logout request failed; clearing client anyway");
      }
      try {
        // A fast re-login can complete while the POST above is still in
        // flight. Tearing down now would clobber the NEW session — clear its
        // token and caches, bouncing the user to /login or a 404. The new
        // sign-in already ran resetRoutesForNewSession(), so skip entirely.
        if (localStorage.getItem("xale_access_token") !== outgoingToken) {
          return;
        }
        // `logout()` BEFORE the cache teardown. The tabs query observer stays
        // mounted (AppRouterProvider is above the router), so clearing the
        // cache re-fires it immediately — with the old token still in
        // localStorage that refetch re-cached the OUTGOING tenant's routes,
        // which then bled into the next sign-in. The server POST above has
        // already run, so nothing else needs the token.
        dispatch(logout());
        await resetQueryClientForLogout();
        await persistor.purge();
      } catch (e) {
        console.log(e, "logout cleanup error");
      }
    })();
  };
  return (
    <header
      className="w-full h-header border-b border-[#e3ece8]"
      style={{
        background:
          "linear-gradient(90deg, #edf7f2 0%, #f5faf8 52%, #fcfdfc 100%)",
      }}
    >
      {/* "Viewing as …" strip: inside the app bar so the column's
          --header-height (grown in AppLayout) covers strip + row. */}
      {impersonating && <ImpersonationBanner />}
      <div className="flex items-center justify-between h-[4rem] px-6">
        {headerInfo ? (
          <div
            className={`items-center gap-3 min-w-0 ${
              headerInfo.onBack ? "flex" : "hidden lg:flex"
            }`}
          >
            {headerInfo.onBack && (
              <button
                onClick={headerInfo.onBack}
                className="size-8 rounded-xl bg-[#f7f9f8] border border-[#e6e8e7] flex items-center justify-center shrink-0 hover:bg-[#eef1f0] hover:border-[#d6ebe3] active:scale-95 transition-all duration-150"
              >
                <ArrowLeft className="size-4 text-[#505e59]" />
              </button>
            )}
            <div className="min-w-0">
              <p
                role="heading"
                aria-level={1}
                className={`truncate font-medium text-[#1e302a] ${
                  headerInfo.onBack
                    ? "text-[15px] tracking-[-0.3px]"
                    : "text-[19px] leading-6 tracking-[-0.4px]"
                }`}
              >
                {headerInfo.title}
              </p>
              <p className="truncate text-[11px] font-normal tracking-[-0.22px] text-[#697571]">
                {headerInfo.breadcrumb}
              </p>
            </div>
          </div>
        ) : isLeadsRoute ? (
          <div className="flex items-center gap-3 min-w-0">
            <LeadsBranchTitle />
            <LeadsCountryPicker />
            <LeadsKeyFieldPickers />
            <LeadsStageStatusPicker />
          </div>
        ) : isDynamicsRoute ? (
          <div className="flex items-center gap-3 min-w-0">
            <DynamicsBranchScopePicker routeId={dynamicsRouteId} />
            <DynamicsCountryPicker routeId={dynamicsRouteId} />
            <DynamicsKeyFieldPickers routeId={dynamicsRouteId} />
            <DynamicsStageStatusPicker routeId={dynamicsRouteId} />
          </div>
        ) : isFavoriteViewRoute ? (
          <div className="flex items-center gap-3 min-w-0">
            <FavoriteViewBranchTitle routeId={dynamicsRouteId} />
            <DynamicsCountryPicker routeId={dynamicsRouteId} />
            <DynamicsKeyFieldPickers routeId={dynamicsRouteId} />
            {/* A view saved FROM a stage route IS that stage's list, so its
                header reads like that stage tab: no Stage dropdown, and the
                Status options resolved against the pinned stage. Without it
                the Status picker sat disabled on "Pick a stage first" while
                the drawer chip said "Status: Offer Letter Issued". */}
            {viewPinnedStageId != null ? (
              <DynamicsStageStatusPicker
                routeId={dynamicsRouteId}
                stageId={viewPinnedStageId}
              />
            ) : (
              <FavoriteViewStageStatusPicker routeId={dynamicsRouteId} />
            )}
          </div>
        ) : isSharedViewRoute ? (
          <div className="flex items-center gap-3 min-w-0">
            <SharedViewHeaderTitle routeId={dynamicsRouteId} />
            <DynamicsCountryPicker routeId={dynamicsRouteId} />
            <DynamicsKeyFieldPickers routeId={dynamicsRouteId} />
            <DynamicsStageStatusPicker routeId={dynamicsRouteId} />
          </div>
        ) : isReportRoute ? (
          <div className="flex items-center gap-2 min-w-0">
            <ReportHeaderTitle />
            <ReportViewShareButton />
          </div>
        ) : isDashboardRoute ? (
          <DashboardV2HeaderTitle />
        ) : (
          <div />
        )}

        <div
          className="flex items-center gap-[1.5rem] min-w-0 flex-1 justify-end"
          data-tour="header-tools"
        >
          {isLeadsRoute ? (
            <LeadsHeaderSearch />
          ) : isDynamicsRoute || isFavoriteViewRoute || isSharedViewRoute ? (
            <DynamicsHeaderSearch
              routeId={dynamicsRouteId}
              compact={isFavoriteViewRoute || isSharedViewRoute}
            />
          ) : isAutomationRoute ? (
            <AutomationHeaderSearch />
          ) : isDashboardRoute ? (
            <DashboardHeaderSearch />
          ) : (
            <SearchInput />
          )}
          <div className="flex items-center gap-[1rem]">
            {/* View routes give the header slot to the view name + pickers —
                Ask AI, notifications and support hide to make the room. */}
            {/* The ONE check-in control. With the HR suite on it records
                attendance and lead availability together; otherwise it is the
                lead check-in pill, exactly as before. */}
            {!isFavoriteViewRoute && <PresenceControl />}
            {!isFavoriteViewRoute && <AIToggleButton />}
            <ChatDropdown />
            {!isFavoriteViewRoute && <AlertDropdown />}
            {!isFavoriteViewRoute && <NotificationDropdown />}
            {!isFavoriteViewRoute && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-xl w-[1.5rem] h-[1.5rem]"
                    aria-label="Support on WhatsApp"
                    title="Support on WhatsApp"
                  >
                    <Headphones className="size-5 text-[var(--colorblack-10)]" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Support on WhatsApp</DropdownMenuLabel>
                  {SUPPORT_WHATSAPP_LINES.map((line) => (
                    <DropdownMenuItem key={line.digits} asChild>
                      <a
                        href={`https://wa.me/${line.digits}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cursor-pointer"
                      >
                        <WhatsAppIcon className="mr-2 h-4 w-4 text-[#25D366]" />
                        <span>{line.display}</span>
                      </a>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div
                  className="bg-[var(--color-green-1)] rounded-full size-[3rem] flex items-center justify-center cursor-pointer"
                  data-tour="header-profile"
                >
                  <span className="text-b1 text-[var(--colorblack-10)] tracking-[-0.36px] line-height-[1.5rem]">
                    {getInitials(user?.name)}
                  </span>
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate("/profile")}>
                  <User className="mr-2 h-4 w-4" />
                  <span>User</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={startTour}>
                  <Compass className="mr-2 h-4 w-4" />
                  <span>Take the tour</span>
                </DropdownMenuItem>
                {canSwitchUser && (
                  <DropdownMenuItem onClick={() => setSwitchUserOpen(true)}>
                    <ArrowLeftRight className="mr-2 h-4 w-4" />
                    <span>Switch user</span>
                  </DropdownMenuItem>
                )}
                {impersonating && (
                  <DropdownMenuItem onClick={() => void switchBackToActor()}>
                    <Undo2 className="mr-2 h-4 w-4" />
                    <span>{switchBackLabel}</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-red-600 focus:text-red-600 focus:bg-red-50"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Logout</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      {canSwitchUser && (
        <SwitchUserDialog
          open={switchUserOpen}
          onOpenChange={setSwitchUserOpen}
        />
      )}
    </header>
  );
}
